/**
 * HttpAgent transport + 503 retry logic.
 *
 * Preserves exact behavior from useChat.ts performRun (lines 264-340):
 * - Token refresh from localStorage before every run
 * - X-Session-Id header for ingress consistent-hashing
 * - 503 retry with [250, 750]ms delays, cancel on session switch
 * - Race guard via activeSessionId
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import { HttpAgent } from "@ag-ui/client";
import { useQueryClient } from "@tanstack/react-query";
import type { AGUIEvent } from "@strands/agent-transport";
import { apiFetch } from "@/shared/api";
import { useChatStoreApi } from "../stores/chat-store-context";
import { routeEvent } from "../stores/chat-event-handlers";
import { sessionsQueryOptions } from "../queries/session-queries";
import {
  useSessionAttach,
  beginRunSuppression,
  endRunSuppression,
} from "./use-session-attach";

export function useAgentStream() {
  const storeApi = useChatStoreApi();
  const queryClient = useQueryClient();
  const { attachToSession } = useSessionAttach();
  const agent = useMemo(
    () => new HttpAgent({ url: "/api/runs", headers: {} }),
    [],
  );
  // Per-run flag flipped in `startRun`, cleared on the first event of the
  // run. Used to invalidate caches that depend on backend state which is
  // only built when the run handler constructs `SessionState` — notably
  // `sessionConnectors`, whose `tools[]` payload is empty for a fresh
  // session until MCP clients have actually started.
  const refreshConnectorsOnNextEvent = useRef(false);
  // Flipped by RUN_FINISHED / RUN_ERROR (the AG-UI client synthesises the
  // latter on user-initiated abort too). Read by `startRun`'s finally to
  // decide whether to auto-reattach: if the SSE ended without a terminal
  // event, the connection dropped (network blip, sleep, proxy timeout) but
  // the backend run is likely still alive — reconnecting via /attach picks
  // it back up without requiring the user to refresh.
  const sawTerminalRef = useRef(false);

  // Long-lived subscriber — routes raw AG-UI events to the Zustand store.
  useEffect(() => {
    const { unsubscribe } = agent.subscribe({
      onEvent: ({ event }) => {
        const store = storeApi.getState();
        const evtType = (event as { type?: string }).type;
        if (store.activeSessionId !== agent.threadId) return; // race guard

        if (evtType === "RUN_FINISHED" || evtType === "RUN_ERROR") {
          sawTerminalRef.current = true;
        }

        if (refreshConnectorsOnNextEvent.current) {
          refreshConnectorsOnNextEvent.current = false;
          const sid = agent.threadId;
          if (sid) {
            queryClient.invalidateQueries({
              queryKey: ["sessions", sid, "connectors"],
            });
          }
        }

        // CUSTOM session_title_updated lives outside the chat store — it
        // mutates the React Query sessions cache so the sidebar rerenders.
        const evt = event as { type?: string; name?: string; value?: unknown };
        if (evt.type === "CUSTOM" && evt.name === "session_title_updated") {
          const { sessionId, title } = (evt.value ?? {}) as {
            sessionId?: string;
            title?: string;
          };
          if (sessionId && title) {
            queryClient.setQueryData(sessionsQueryOptions().queryKey, (old) =>
              old?.map((s) => (s.id === sessionId ? { ...s, title } : s)),
            );
          }
        }

        routeEvent(
          event as unknown as AGUIEvent,
          (fn) => storeApi.setState(fn),
          () => storeApi.getState(),
          agent.threadId ?? null,
        );
      },
    });
    return () => unsubscribe();
  }, [agent, queryClient, storeApi]);

  /**
   * Start an agent run. Caller must update messages/state in the store
   * BEFORE calling this (batched update for HITL).
   */
  const startRun = useCallback(
    async (
      targetSession: string,
      inputMessages: Parameters<typeof agent.setMessages>[0],
      extraForwardedProps?: Record<string, unknown>,
    ) => {
      const store = storeApi.getState();

      // A live POST /runs stream and an attach reader both consume the same
      // backend broker. loadHistory unconditionally opens an attach on every
      // session load, and the backend /attach polls up to 3s for a broker —
      // long enough that on a freshly-created session the user's first send
      // races into that window and attach "catches" this very run. Both
      // consumers would then route every event, doubling each TEXT delta.
      // Killing any in-flight attach before we open the run guarantees a
      // single consumer. A guard (not a one-shot abort) is required because
      // loadHistory opens its attach asynchronously and may register it
      // AFTER we'd have aborted — the guard rejects it regardless of order.
      beginRunSuppression(targetSession);

      // Point agent at this session
      agent.threadId = targetSession;

      // Refresh auth header — token may have rotated
      const token = localStorage.getItem("token");
      const modelOverride = localStorage.getItem("modelOverride");
      agent.headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "X-Session-Id": targetSession,
        ...(modelOverride ? { "X-Model-Override": modelOverride } : {}),
      };

      // Seed SDK with turn inputs
      agent.setState(store.appState);
      agent.setMessages(inputMessages);

      storeApi.setState((s) => {
        s.streaming = true;
        s.inReasoning = false;
      });

      // Trigger a `sessionConnectors` refetch on the first event of this
      // run — by then the backend has built `SessionState` with MCP tools
      // registered, so the response will carry tool metadata (titles).
      refreshConnectorsOnNextEvent.current = true;
      // Reset terminal tracker; flipped by the subscriber when it sees
      // RUN_FINISHED / RUN_ERROR. If the SSE ends without one, we treat
      // it as a dropped connection and auto-reattach in the finally.
      sawTerminalRef.current = false;

      const runOnce = () =>
        agent.runAgent({
          runId: crypto.randomUUID(),
          tools: storeApi.getState().pendingTools,
          context: [],
          forwardedProps: {
            ...storeApi.getState().runtimeForwardedProps,
            ...extraForwardedProps,
          },
        });

      // 503 retry — [250ms, 750ms] then give up
      const RETRY_DELAYS_MS = [250, 750];

      try {
        try {
          await runOnce();
        } catch (e) {
          const status = (e as { status?: number }).status;
          if (status !== 503) throw e;
          let lastErr: unknown = e;
          for (const delay of RETRY_DELAYS_MS) {
            await new Promise((r) => setTimeout(r, delay));
            if (storeApi.getState().activeSessionId !== targetSession) return;
            try {
              await runOnce();
              lastErr = null;
              break;
            } catch (retryErr) {
              lastErr = retryErr;
              if ((retryErr as { status?: number }).status !== 503) break;
            }
          }
          if (lastErr) throw lastErr;
        }
      } catch (e) {
        if ((e as Error).name !== "AbortError") {
          console.error("Run error:", e);
        }
      } finally {
        // Lift the attach ban first — the recovery branch below legitimately
        // re-attaches when the SSE dropped without a terminal event, and the
        // F5-recovery attach must work once the live run is over.
        endRunSuppression(targetSession);
        if (storeApi.getState().activeSessionId === targetSession) {
          if (sawTerminalRef.current) {
            storeApi.setState((s) => {
              s.streaming = false;
            });
          } else {
            // SSE ended without RUN_FINISHED / RUN_ERROR — connection
            // dropped (network blip, laptop sleep, proxy idle timeout).
            // The backend run keeps going on its own thread; reattach
            // and let the replay buffer rebuild what we missed. Attach
            // sets streaming=false on its own exit path, so we don't
            // reset it here — leaving it true keeps the spinner up
            // through the brief reconnect window.
            attachToSession(targetSession);
          }
        }
      }
    },
    [agent, storeApi, attachToSession],
  );

  const stopStreaming = useCallback(() => {
    // Tell the backend to flip the cooperative stop flag so it doesn't keep
    // burning LLM tokens after we close the SSE connection. Fire-and-forget:
    // if the request fails (network blip, 404 on fresh session), the
    // subsequent abortRun() still unsticks the UI. The backend also pushes
    // a RunError onto the queue so any other tab watching the stream sees
    // the stop immediately.
    const sessionId = agent.threadId;
    if (sessionId) {
      apiFetch(`/api/sessions/${encodeURIComponent(sessionId)}/stop`, {
        method: "POST",
      }).catch(() => {});
    }
    agent.abortRun();
  }, [agent]);

  return { startRun, stopStreaming };
}
